import { useState, useEffect } from 'react';
import { ErrorBoundary } from '../components/ErrorBoundary';
import {
  Outlet,
  NavLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  Settings2,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Star,
  UserCircle,
  TrendingUp,
  Wrench,
  Palette,
  Megaphone,
  ShoppingBag,
} from 'lucide-react';
import { useUserStore } from '../store/userStore';
import { useEffectiveRole } from '../hooks/useEffectiveRole';
import { isPersonaGated } from '../lib/personas';
import { usePresence } from '../hooks/usePresence';
import { Logo } from '../components/Logo';
import { AvatarMenu } from '../components/AvatarMenu';
import { WelcomeModal } from '../components/WelcomeModal';
import { OnboardingDemo } from '../components/onboarding/OnboardingDemo';
import { NotificationBell, NotificationPanel } from '../components/NotificationBell';
import { FeedbackButton } from '../components/feedback/FeedbackButton';
import { useOnboardingStore } from '../store/onboardingStore';
import { useDemoStore } from '../store/demoStore';
import { useRegularProjects } from '../hooks/useProjects';
import { usePublicFlags } from '../hooks/usePublicFlags';
import type { FunctionalRole } from '../types';

// Play a silent 1-sample buffer to satisfy the browser autoplay policy while
// still inside a user-gesture, so the Guided Tour's narration audio can play.
function unlockAudio() {
  try {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const buf = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start(0);
    void ctx.resume();
  } catch { /* ignore */ }
}

const ROLE_ICONS: Record<string, React.ElementType> = {
  executive:       TrendingUp,
  product_manager: LayoutDashboard,
  team_lead:       Users,
  engineer:        Wrench,
  designer:        Palette,
  sales:           ShoppingBag,
  marketing:       Megaphone,
};

const ROLE_LABELS: Record<string, string> = {
  executive:       'Executive',
  product_manager: 'Product Manager',
  team_lead:       'Team Lead',
  engineer:        'Engineer',
  designer:        'Designer',
  sales:           'Sales',
  marketing:       'Marketing',
};

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  end?: boolean;
  roles: string[];
}

const ALL_NAV_ITEMS: NavItem[] = [
  // TODO: { to: '/portfolio', label: 'Portfolio', icon: Briefcase, roles: ['executive', 'product_manager', 'admin', 'team_lead', 'sales'] },
  // TODO: { to: '/my-work', label: 'My Work', icon: CheckSquare, roles: ['engineer', 'designer', 'team_lead'] },
  // TODO: { to: '/insights', label: 'Insights', icon: Sparkles, roles: ['product_manager', 'executive', 'admin'] },
  // TODO: { to: '/reports', label: 'Reports', icon: FileText, roles: ['product_manager', 'executive', 'admin', 'sales'] },
  // TODO: { to: '/team', label: 'Team', icon: Users, roles: ['team_lead', 'admin'] },
  // TODO: { to: '/launches', label: 'Launches', icon: Rocket, roles: ['marketing', 'sales'] },
  { to: '/settings', label: 'Settings', icon: Settings2, roles: [] },
];

function SidebarContent({
  collapsed,
  onToggle, // eslint-disable-line @typescript-eslint/no-unused-vars
  onClose,
  onOpenNotif,
}: {
  collapsed: boolean;
  onToggle: () => void;
  onClose?: () => void;
  onOpenNotif: (rect: DOMRect) => void;
}) {
  const navigate = useNavigate();
  const { currentUser, sessionConfig, setActiveRole } = useUserStore();

  const legacyPersonasEnabled = sessionConfig?.legacyPersonasEnabled ?? false;
  // Gated roles get no nav entry — their dashboard doesn't resolve while the
  // flag is off (see RoleDashboard.tsx), so a nav entry for one would
  // dead-end into PMDashboard under a mismatched label. See lib/personas.ts.
  const functionalRoles: FunctionalRole[] = (
    currentUser?.functionalRoles?.length
      ? currentUser.functionalRoles
      : sessionConfig?.functionalRoles ?? []
  ).filter((role) => !isPersonaGated(role, legacyPersonasEnabled));
  const effectiveRole = useEffectiveRole();

  const isAdmin = sessionConfig?.entityRole === 'admin' || sessionConfig?.role === 'admin';

  const visibleNavItems = ALL_NAV_ITEMS.filter((item) =>
    item.roles.length === 0 || isAdmin || item.roles.includes(effectiveRole)
  );

  return (
    <div className="flex flex-col h-full">
      <div
        className={`flex items-center h-20 px-4 flex-shrink-0 ${
          collapsed ? 'justify-center' : 'justify-between'
        }`}
      >
        <Logo collapsed={collapsed} className="h-8 w-auto text-gray-900 dark:text-white flex-shrink-0" />
      </div>

      <nav className="flex-1 px-2 space-y-0.5 overflow-y-auto">
        {/* Dashboards section — one entry per functional role, order fixed */}
        {(() => {
          const dashboards = [...functionalRoles]; // keep original order (already gating-filtered)
          // A gated primaryRole (e.g. 'designer' while the flag is off)
          // wouldn't appear in `dashboards` at all, so the star would never
          // land on anything — fall through to the first surviving role
          // instead of a role that isn't rendered here.
          const primaryR =
            (currentUser?.primaryRole && !isPersonaGated(currentUser.primaryRole, legacyPersonasEnabled)
              ? currentUser.primaryRole
              : null) ?? functionalRoles[0] ?? null;
          if (dashboards.length === 0) {
            // No functional roles — show a single generic Home
            return (
              <div className={`rounded-xl border border-sky-400/30 ${collapsed ? 'p-1' : 'p-1 mb-1'}`}>
                <NavLink
                  to="/"
                  end
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg focus-visible:ring-2 focus-visible:ring-primary/50 transition-all text-meta font-medium ${
                      collapsed ? 'justify-center' : ''
                    } ${isActive
                      ? 'bg-primary/10 dark:bg-primary/20 text-primary'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06]'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <LayoutDashboard className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-primary' : ''}`} />
                      {!collapsed && <span>Home</span>}
                    </>
                  )}
                </NavLink>
              </div>
            );
          }
          return (
            <div className={`rounded-xl border border-sky-400/30 ${collapsed ? 'p-1' : 'p-1 mb-1'}`}>
              {!collapsed && dashboards.length > 1 && (
                <p className="text-micro font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 px-3 pt-1 pb-0.5">
                  Dashboards
                </p>
              )}
              <div className="space-y-0.5">
                {dashboards.map((role) => {
                  const RoleIcon = ROLE_ICONS[role] ?? UserCircle;
                  const isActive = effectiveRole === role;
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() => {
                        setActiveRole(role as FunctionalRole);
                        navigate('/');
                        onClose?.();
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg focus-visible:ring-2 focus-visible:ring-primary/50 transition-all text-meta font-medium ${
                        collapsed ? 'justify-center' : ''
                      } ${isActive
                        ? 'bg-primary/10 dark:bg-primary/20 text-primary'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06]'
                      }`}
                      title={collapsed ? ROLE_LABELS[role] ?? role : undefined}
                    >
                      <RoleIcon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-primary' : ''}`} />
                      {!collapsed && (
                        <span className="flex-1 text-left">{ROLE_LABELS[role] ?? role.replace(/_/g, ' ')}</span>
                      )}
                      {!collapsed && role === primaryR && (
                        <Star className="h-4 w-4 text-amber-400 fill-amber-400 flex-shrink-0" aria-label="Primary role" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}
        {visibleNavItems.length > 0 && <div className="border-t border-gray-100 dark:border-white/[0.06] my-1" />}

        {visibleNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl focus-visible:ring-2 focus-visible:ring-primary/50 transition-all text-meta font-medium ${
                collapsed ? 'justify-center' : ''
              } ${
                isActive
                  ? 'bg-primary/10 dark:bg-primary/20 text-primary'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06]'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <item.icon
                  className={`h-5 w-5 flex-shrink-0 ${
                    isActive ? 'text-primary' : ''
                  }`}
                />
                {!collapsed && <span>{item.label}</span>}
              </>
            )}
          </NavLink>
        ))}

      </nav>

      <div className="border-t border-gray-100 dark:border-white/[0.06] px-2 py-3 space-y-4 flex-shrink-0">
        <NotificationBell collapsed={collapsed} onOpen={onOpenNotif} />

        <FeedbackButton collapsed={collapsed} />

        <AvatarMenu
          user={currentUser}
          name={currentUser?.fullName ?? sessionConfig?.entityName ?? 'User'}
          subtitle={(() => {
            const functionalRoles = currentUser?.functionalRoles ?? sessionConfig?.functionalRoles;
            const entityRole = currentUser?.entityRole ?? sessionConfig?.entityRole;
            const role = currentUser?.role ?? sessionConfig?.role;
            return functionalRoles?.length
              ? functionalRoles[0].replace(/_/g, ' ')
              : entityRole ?? role?.replace(/_/g, ' ') ?? '';
          })()}
          collapsed={collapsed}
          onNavigate={onClose}
        />
      </div>
    </div>
  );
}

export function AppShell() {
  usePresence();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, sessionConfig, setUser } = useUserStore();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifRect, setNotifRect] = useState<DOMRect | null>(null);

  // ── Onboarding / Guided Tour ────────────────────────────────────────────
  // Lives here (always mounted, route- and role-agnostic) rather than in the
  // dashboards, so the avatar menu's "Restart Guided Tour" works from any
  // view — including TodayView, which is the default RoleDashboard landing.
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeInitialStep, setWelcomeInitialStep] = useState(0);
  const [welcomeGoogleConnected, setWelcomeGoogleConnected] = useState(false);
  const replayRequested = useOnboardingStore((s) => s.replayRequested);
  const clearReplay = useOnboardingStore((s) => s.clearReplay);

  // Audio Guided Tour — gated by the onboarding_demo public flag. When on, the
  // demo launches after the Welcome card is dismissed (that click also unlocks
  // audio autoplay), on first login and from "Restart Guided Tour".
  const { onboardingDemo } = usePublicFlags();
  const { data: projects } = useRegularProjects();
  const demoActive = useDemoStore((s) => s.active);
  const demoFirstProjectId = useDemoStore((s) => s.firstProjectId);
  const startDemo = useDemoStore((s) => s.start);
  const stopDemo = useDemoStore((s) => s.stop);

  // First-time onboarding + return-from-Google-OAuth
  useEffect(() => {
    if (!currentUser) return;
    if (searchParams.get('google_connected') === '1') {
      navigate('/', { replace: true });
      setWelcomeInitialStep(1);
      setWelcomeGoogleConnected(true);
      setShowWelcome(true);
      return;
    }
    if (!currentUser.onboardingCompletedAt) {
      setShowWelcome(true);
    }
  }, [currentUser, searchParams, navigate]);

  // Manual replay from the avatar menu — always shows, ignores the seen flag
  useEffect(() => {
    if (replayRequested) {
      setWelcomeInitialStep(0);
      setWelcomeGoogleConnected(false);
      setShowWelcome(true);
      clearReplay();
    }
  }, [replayRequested, clearReplay]);

  const handleWelcomeComplete = () => {
    // This runs inside the Welcome card's click handler — unlock audio here so
    // the tour narration can autoplay when the demo starts.
    unlockAudio();
    import('../lib/api').then(({ completeOnboarding }) => {
      void completeOnboarding().then((updated) => setUser(updated));
    });
    setShowWelcome(false);
    setWelcomeInitialStep(0);
    setWelcomeGoogleConnected(false);
    if (onboardingDemo) {
      const firstProject = (projects ?? [])[0];
      startDemo(firstProject?.projectId);
    }
  };

  const handleDemoComplete = () => {
    stopDemo();
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-bg-deep overflow-hidden">
      {notifOpen && <NotificationPanel onClose={() => setNotifOpen(false)} anchorRect={notifRect} />}
      <WelcomeModal
        isOpen={showWelcome}
        onClose={handleWelcomeComplete}
        onComplete={handleWelcomeComplete}
        sessionConfig={sessionConfig}
        initialStep={welcomeInitialStep}
        initialGoogleConnected={welcomeGoogleConnected}
      />
      {/* Desktop sidebar — z-10 so toggle button renders above main panel */}
      <motion.aside
        animate={{ width: collapsed ? '5rem' : '16rem' }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className="relative hidden lg:flex flex-col flex-shrink-0 z-10"
      >
        {/* Inner wrapper provides overflow clipping during width animation */}
        <div className="flex flex-col h-full bg-white dark:bg-bg-surface overflow-hidden">
          <SidebarContent
            collapsed={collapsed}
            onToggle={() => setCollapsed((p) => !p)}
            onOpenNotif={(r) => { setNotifRect(r); setNotifOpen(true); }}
          />
        </div>

        {/* Toggle button lives outside the overflow-hidden wrapper so it's never clipped */}
        <button
          onClick={() => setCollapsed((p) => !p)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          className="absolute -right-3 top-7 z-20 h-6 w-6 bg-white dark:bg-bg-surface border border-gray-200 dark:border-white/10 rounded-full hidden lg:flex items-center justify-center text-gray-400 hover:text-gray-900 dark:hover:text-white focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors shadow-sm"
        >
          {collapsed
            ? <ChevronRight className="h-4 w-4" />
            : <ChevronLeft className="h-4 w-4" />}
        </button>
      </motion.aside>

      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 bg-black/60 z-40 lg:hidden"
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed left-0 top-0 bottom-0 w-64 bg-white dark:bg-bg-surface border-r border-gray-100 dark:border-white/[0.06] z-50 lg:hidden"
            >
              <SidebarContent
                collapsed={false}
                onToggle={() => setMobileOpen(false)}
                onClose={() => setMobileOpen(false)}
                onOpenNotif={(r) => { setNotifRect(r); setNotifOpen(true); }}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content — z-0 creates its own stacking context below the aside's z-10 */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative z-0">
        {/* Mobile header */}
        <header className="lg:hidden flex items-center h-16 px-4 bg-white dark:bg-bg-surface border-b border-gray-100 dark:border-white/[0.06] flex-shrink-0 gap-2">
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
            className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="ml-1">
            <Logo className="h-7 w-auto text-gray-900 dark:text-white" />
          </div>
          <div className="ml-auto flex items-center gap-1">
            <NotificationBell collapsed={true} onOpen={(r) => { setNotifRect(r); setNotifOpen(true); }} />
            <button
              onClick={() => setMobileOpen(false)}
              className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10"
              style={{ display: mobileOpen ? 'block' : 'none' }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>


        <main className="flex-1 overflow-y-auto">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {demoActive && (
        <OnboardingDemo
          firstProjectId={demoFirstProjectId}
          onComplete={handleDemoComplete}
          navigate={navigate}
        />
      )}
    </div>
  );
}
